/** @jsx jsx */
import React, { useMemo } from 'react';
import { styled, css, jsx } from '@compiled/react';
import noop from 'lodash/noop';
import Button from '@atlaskit/button';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl/src/index.tsx';
import { Link } from '@atlassian/react-resource-router';
import type { Series } from '../../common/summary/types.tsx';
import { getFormattedLabel } from '../../common/summary/utils';
import messages from './messages';

type Props = {
	isEditable?: boolean;
	chartSeries?: Series[];
	reportLink?: string;
	onEditSeriesClick?: (seriesIndex: number) => void;
	onRemoveSeriesClick?: (seriesIndex: number) => void;
};

const seriesActionContainerStyles = css({ margin: 0 });

const OverallSummary = ({
	isEditable = false,
	onEditSeriesClick = noop,
	onRemoveSeriesClick = noop,
	chartSeries = [],
	reportLink,
}: Props) => {
	const { formatMessage } = useIntl();
	const list = useMemo(
		() =>
			chartSeries.map(({ seriesIndex, name, summaryValue: { value, valueType }, itemStyle }) => (
				<LabelValuePair key={seriesIndex}>
					{reportLink ? (
						<Link href={reportLink}>
							<Label itemStyle={itemStyle}>
								{getFormattedLabel(valueType, value, formatMessage)}
							</Label>
						</Link>
					) : (
						<Label itemStyle={itemStyle}>
							{getFormattedLabel(valueType, value, formatMessage)}
						</Label>
					)}
					<p>{name}</p>
					{isEditable && (
						<Inline>
							<li
								data-test-id="servicedesk-reports-common.ui.overall-summary.edit-series"
								css={seriesActionContainerStyles}
							>
								<Button
									appearance="link"
									onClick={() => {
										onEditSeriesClick(seriesIndex);
									}}
									spacing="none"
								>
									{formatMessage(messages.editLabel)}
								</Button>
							</li>
							<li
								data-test-id="servicedesk-reports-common.ui.overall-summary.remove-series"
								css={seriesActionContainerStyles}
							>
								<Button
									appearance="link"
									onClick={() => {
										onRemoveSeriesClick(seriesIndex);
									}}
								>
									{formatMessage(messages.removeLabel)}
								</Button>
							</li>
						</Inline>
					)}
				</LabelValuePair>
			)),
		[chartSeries, formatMessage, isEditable, onEditSeriesClick, onRemoveSeriesClick, reportLink],
	);
	return (
		<Inline data-test-id="servicedesk-reports-common.ui.overall-summary.label-value-list">
			{list}
		</Inline>
	);
};

export default OverallSummary;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Inline = styled.ul({
	display: 'flex',
	flexWrap: 'wrap',
	alignItems: 'center',
	listStyleType: 'none',
	padding: '0 0',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LabelValuePair = styled.li({
	alignSelf: 'flex-start',
	margin: `${token('space.250', '20px')} ${token('space.500', '40px')} ${token(
		'space.250',
		'20px',
	)} 0`,
	maxWidth: '120px',
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
const Label = styled.h2<{ itemStyle: any }>((props) => ({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	color: props.itemStyle.color,
}));
